// src/features/analytics/ShareDashboardDialog.tsx
// Change a dashboard's visibility. ShareDashboard.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { analyticsApi } from '@core/api';
import type { Dashboard, DashboardVisibility } from '@core/types/analytics';
import { AnalyticsModal } from './Modal';
import { DASHBOARD_VISIBILITIES, errMessage } from './helpers';

interface ShareDashboardDialogProps {
  open: boolean;
  onClose: () => void;
  dashboard: Dashboard | null;
  onSaved: (dashboard: Dashboard) => void;
}

export function ShareDashboardDialog({ open, onClose, dashboard, onSaved }: ShareDashboardDialogProps) {
  const { t } = useI18n();
  const [visibility, setVisibility] = useState<DashboardVisibility>('DASHBOARD_VISIBILITY_PRIVATE');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setVisibility(dashboard?.visibility ?? 'DASHBOARD_VISIBILITY_PRIVATE');
      setError(null);
    }
  }, [open, dashboard]);

  const save = async () => {
    if (!dashboard?.entity?.id) return;
    setSaving(true);
    setError(null);
    try {
      const res = await analyticsApi.ShareDashboard(dashboard.entity.id, { dashboard: dashboard.entity, visibility });
      if (res.dashboard) onSaved(res.dashboard);
      onClose();
    } catch (e) {
      setError(errMessage(e, t('analytics.dashboards.shareError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnalyticsModal
      open={open}
      onClose={onClose}
      title={t('analytics.dashboards.share')}
      widthClass="max-w-md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? t('common.saving') : t('analytics.dashboards.share')}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      <fieldset className="space-y-2">
        <legend className="mb-1 text-xs font-medium text-muted-foreground">{t('analytics.dashboards.visibility')}</legend>
        {DASHBOARD_VISIBILITIES.map((v) => (
          <label key={v} className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm hover:bg-muted">
            <input type="radio" name="visibility" checked={visibility === v} onChange={() => setVisibility(v)} />
            <span className="text-foreground">{t(`analytics.visibility.${v}`)}</span>
          </label>
        ))}
      </fieldset>
    </AnalyticsModal>
  );
}
